import React from 'react';
import PropTypes from 'prop-types';

const DEFAULT_POLLING_TIME = {
  INTERVAL: 1000,
  STOP: 30000,
};

const calculateBackoffTime = factor => factor ** 3 + 5 + Math.random() * 10 * (factor + 1);

const withPolling = (pollAction, hasPollActionFinished, options = {}) => Component => {
  class WrappedComponent extends React.Component {
    constructor(props) {
      super(props);
      this.isPolling = false;
      this.timeoutId = null;
      this.pollCount = 0;

      this.exponencialBackoffTime = props => {
        const errorCount = options.errorCount ? options.errorCount(props) : 0;

        if (errorCount) {
          const backoffTime = calculateBackoffTime(errorCount);
          return options.maxDelayTime && backoffTime > options.maxDelayTime
            ? options.maxDelayTime
            : backoffTime;
        } else if (options.backoffOnTimeProgress) {
          const backoffTime = calculateBackoffTime(this.pollCount);

          if (options.maxDelayTime && backoffTime > options.maxDelayTime) {
            return options.maxDelayTime;
          } else if (backoffTime < options.minDelayTime) {
            return options.minDelayTime;
          } else {
            return backoffTime;
          }
        } else {
          return 0;
        }
      };

      this.intervalCriteria =
        options.intervalCriteria ||
        function() {
          return DEFAULT_POLLING_TIME.INTERVAL;
        };
      this.startCriteria =
        options.startCriteria ||
        function() {
          return true;
        };
      this.exitCriteria =
        options.exitCriteria ||
        function() {
          return false;
        };
      this.stopAfterTime = options.stopAfterTime || false;
      this.onExit = options.onExit || function() {};
    }

    componentDidMount() {
      if (this.startCriteria(null, this.props)) {
        this.startPoll(this.props);
      }
    }

    // When we unmount the polling component, make sure to clear the
    // pending timeout
    componentWillUnmount() {
      if (this.timeoutId !== null) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }

    componentDidUpdate(prevProps) {
      // If the poll ACTION is already finished
      // If the poll is not finished yet, we also skip this
      if (hasPollActionFinished(prevProps, this.props) && this.isPolling) {
        // Check if there is a poll already "enqueued"
        // If another poll action is enqueued, we just skip this update
        if (this.timeoutId === null) {
          // If no poll is enqueued, enqueue a new one
          this.doDelayedPoll(prevProps, this.props);
        }
        return;
      }

      // If we are not polling but we should start polling, trigger polling start
      if (!this.isPolling && this.startCriteria(prevProps, this.props)) {
        this.startPoll(prevProps);
      }
    }

    stopPoll() {
      // Stop polling
      this.isPolling = false;
      this.pollCount = 0;
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      this.onExit(this.props);
    }

    startPoll(prevProps) {
      // Start polling
      this.isPolling = true;
      if (this.stopAfterTime) {
        setTimeout(() => {
          this.stopPoll();
        }, this.stopAfterTime);
      }
      this.doDelayedPoll(prevProps, this.props);
    }

    doDelayedPoll(prevProps, props) {
      // Do a delayed poll
      this.timeoutId = setTimeout(
        () => this.doPoll(prevProps),
        this.exponencialBackoffTime(prevProps) || this.intervalCriteria(prevProps, props),
      );
    }

    doPoll(prevProps) {
      // If there is a timeout set, clear it
      if (this.timeoutId !== null) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }

      // If we got the stop poll criteria, trigger stop poll
      if (this.exitCriteria(prevProps, this.props)) {
        return this.stopPoll();
      }

      // Continue polling
      this.pollCount += 1;
      pollAction(this.props);
    }

    render() {
      const props = this.props;
      return <Component {...props} />;
    }
  }

  withPolling.propTypes = {
    hasPollActionFinished: PropTypes.func.isRequired,
    pollAction: PropTypes.func.isRequired,
    options: PropTypes.object,
  };

  return WrappedComponent;
};

export default withPolling;
